import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import './ProfilePage.css';

const ProfilePage = () => {
  const { userType } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm();

  // Mock user data - would be fetched from API in production
  const mockUserData = {
    username: 'johndoe',
    email: 'john.doe@leangains.com',
    firstName: 'John',
    lastName: 'Doe',
    password: '********'
  };

  useEffect(() => {
    // Simulate loading user data
    setIsLoading(true);
    setTimeout(() => {
      setValue('username', mockUserData.username);
      setValue('email', mockUserData.email);
      setValue('firstName', mockUserData.firstName);
      setValue('lastName', mockUserData.lastName);
      setValue('password', mockUserData.password);
      setIsLoading(false);
    }, 500);
  }, [setValue, mockUserData.username, mockUserData.email, mockUserData.firstName, mockUserData.lastName, mockUserData.password, setIsLoading]);

  const onSubmit = (data) => {
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      console.log('Profile updated:', data);
      setSuccessMessage('Profile updated successfully!');
      setIsLoading(false);
      setIsEditing(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    }, 1000);
  };

  const handleCancel = () => {
    // Reset form to original values
    reset({
      username: mockUserData.username,
      email: mockUserData.email,
      firstName: mockUserData.firstName,
      lastName: mockUserData.lastName,
      password: mockUserData.password
    });
    setIsEditing(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h1 className="page-title">User Profile</h1>
        <p className="page-description">
          View and manage your personal information and account settings.
        </p>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card full-width">
          <div className="card-header">
            <h2 className="card-title">Profile Information</h2>
            {!isEditing && (
              <button 
                className="btn btn-primary" 
                onClick={() => setIsEditing(true)}
              >
                Edit Profile
              </button>
            )}
          </div>
          <div className="card-content">
            {isLoading ? (
              <div className="loading-spinner-container">
                <div className="loading-spinner"></div>
                <p>Loading profile information...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="profile-form">
                {successMessage && (
                  <div className="success-message" role="alert">
                    {successMessage}
                  </div>
                )}
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="username">
                      Username <span className="required">*</span>
                    </label>
                    <input
                      id="username"
                      type="text"
                      disabled={!isEditing}
                      className={errors.username ? 'error' : ''}
                      {...register('username', {
                        required: 'Username is required',
                        minLength: {
                          value: 3,
                          message: 'Username must be at least 3 characters'
                        }
                      })}
                    />
                    {errors.username && (
                      <span className="field-error">
                        {errors.username.message}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">
                      Email <span className="required">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      disabled={!isEditing}
                      className={errors.email ? 'error' : ''}
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                    />
                    {errors.email && (
                      <span className="field-error">
                        {errors.email.message}
                      </span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">
                      First Name <span className="required">*</span>
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      disabled={!isEditing}
                      className={errors.firstName ? 'error' : ''}
                      {...register('firstName', {
                        required: 'First name is required'
                      })}
                    />
                    {errors.firstName && (
                      <span className="field-error">
                        {errors.firstName.message}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="lastName">
                      Last Name <span className="required">*</span>
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      disabled={!isEditing}
                      className={errors.lastName ? 'error' : ''}
                      {...register('lastName', {
                        required: 'Last name is required'
                      })}
                    />
                    {errors.lastName && (
                      <span className="field-error">
                        {errors.lastName.message}
                      </span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group password-group">
                    <label htmlFor="password">
                      Password <span className="required">*</span>
                    </label>
                    <div className="password-input-container">
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        disabled={!isEditing}
                        className={errors.password ? 'error' : ''}
                        {...register('password', {
                          required: 'Password is required',
                          minLength: {
                            value: 6,
                            message: 'Password must be at least 6 characters'
                          }
                        })}
                      />
                      {isEditing && (
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={togglePasswordVisibility}
                        >
                          {showPassword ? (
                            <svg viewBox="0 0 20 20" fill="currentColor">
                              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                              <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                              <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                    {errors.password && (
                      <span className="field-error">
                        {errors.password.message}
                      </span>
                    )}
                  </div>
                </div>

                {isEditing && (
                  <div className="form-actions">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner"></span>
                          <span>Saving...</span>
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCancel}
                      disabled={isLoading}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-header">
            <h2 className="card-title">Account Information</h2>
          </div>
          <div className="card-content">
            <div className="account-info">
              <div className="info-item">
                <span className="info-label">Account Type</span>
                <span className="info-value">
                  <span className={`role-badge ${userType?.toLowerCase() || 'user'}`}>
                    {userType || 'User'}
                  </span>
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Account Created</span>
                <span className="info-value">January 15, 2024</span>
              </div>
              <div className="info-item">
                <span className="info-label">Last Login</span>
                <span className="info-value">Today at 10:30 AM</span>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-header">
            <h2 className="card-title">Security Settings</h2>
          </div>
          <div className="card-content">
            <div className="security-settings">
              <div className="setting-item">
                <div className="setting-info">
                  <h3 className="setting-title">Two-Factor Authentication</h3>
                  <p className="setting-description">
                    Add an extra layer of security to your account
                  </p>
                </div>
                <button className="btn btn-outline">Enable</button>
              </div>
              <div className="setting-item">
                <div className="setting-info">
                  <h3 className="setting-title">Session Management</h3>
                  <p className="setting-description">
                    Manage your active login sessions
                  </p>
                </div>
                <button className="btn btn-outline">Manage</button>
              </div>
              <div className="setting-item">
                <div className="setting-info">
                  <h3 className="setting-title">Password Reset</h3>
                  <p className="setting-description">
                    Change your account password
                  </p>
                </div>
                <button className="btn btn-outline">Reset</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
